import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';

import {
  createQueuePattern,
  deleteQueuePattern,
  fetchQueuePatterns,
  QueueServicePattern,
  updateQueuePattern,
} from '../../api/queuePatterns';
import { fetchQueueOptions, QueueListItem } from '../../api/queues';
import QueuePatternFormDrawer, {
  QueueOption,
  QueuePatternFormValues,
} from './components/QueuePatternFormDrawer';

const SequencePreview: React.FC<{ sequence: string[]; queueMap: Map<string, QueueOption> }> = ({
  sequence,
  queueMap,
}) => (
  <Stack direction="row" spacing={1} flexWrap="wrap">
    {sequence.map((queueId, index) => {
      const queue = queueMap.get(queueId);
      const label = queue
        ? `${index + 1}. ${queue.name}${queue.code ? ` (${queue.code})` : ''}`
        : `${index + 1}. ${queueId}`;

      return <Chip key={`${queueId}-${index}`} label={label} size="small" />;
    })}
  </Stack>
);

const mapQueueOptions = (queues: QueueListItem[] = []): QueueOption[] =>
  queues.map((queue) => ({
    id: queue.id,
    name: queue.name,
    code: queue.code,
  }));

const QueuePatternsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPattern, setEditingPattern] = useState<QueueServicePattern | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [patternToDelete, setPatternToDelete] = useState<QueueServicePattern | null>(null);

  const {
    data: patternsData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery(['queue-service-patterns', includeInactive ? 'with-inactive' : 'only-active'], () => fetchQueuePatterns(includeInactive), {
    keepPreviousData: true,
    staleTime: 30_000,
  });

  const { data: queueOptionsData } = useQuery(['queue-pattern-queue-options'], () => fetchQueueOptions({ limit: 200 }), {
    staleTime: 5 * 60 * 1000,
  });

  const queueOptions = useMemo(() => mapQueueOptions(queueOptionsData), [queueOptionsData]);
  const queueMap = useMemo(() => {
    const map = new Map<string, QueueOption>();
    queueOptions.forEach((queue) => map.set(queue.id, queue));
    return map;
  }, [queueOptions]);

  const patterns = patternsData ?? [];

  const createMutation = useMutation(createQueuePattern);
  const updateMutation = useMutation(({ id, payload }: { id: string; payload: QueuePatternFormValues }) =>
    updateQueuePattern(id, payload),
  );
  const deleteMutation = useMutation(deleteQueuePattern);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingPattern(null);
  };

  const invalidatePatterns = () => {
    queryClient.invalidateQueries('queue-service-patterns');
  };

  const handleSubmit = async (values: QueuePatternFormValues) => {
    try {
      if (editingPattern) {
        await updateMutation.mutateAsync({ id: editingPattern.id, payload: values });
        toast.success('Шаблон обновлён');
      } else {
        await createMutation.mutateAsync(values);
        toast.success('Шаблон создан');
      }

      invalidatePatterns();
      closeDrawer();
    } catch (mutationError) {
      const message = mutationError instanceof Error ? mutationError.message : 'Не удалось сохранить шаблон';
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!patternToDelete) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(patternToDelete.id);
      toast.success('Шаблон удалён');
      invalidatePatterns();
      setPatternToDelete(null);
    } catch (mutationError) {
      const message = mutationError instanceof Error ? mutationError.message : 'Не удалось удалить шаблон';
      toast.error(message);
    }
  };

  const loadingError = isError ? (error as Error) : null;

  return (
    <Box p={3}>
      <Helmet>
        <title>Шаблоны обслуживания очередей — ELEKTROM</title>
      </Helmet>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} mb={3}>
        <Box flex={1}>
          <Typography variant="h4" component="h1" gutterBottom>
            Шаблоны обслуживания очередей
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Управляйте порядком вызова талонов между несколькими очередями и создавайте последовательности обслуживания.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(event) => setIncludeInactive(event.target.checked)}
              />
            }
            label="Показывать неактивные"
          />
          <Tooltip title="Обновить список">
            <span>
              <IconButton onClick={() => refetch()} disabled={isFetching}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingPattern(null);
              setDrawerOpen(true);
            }}
            disabled={queueOptions.length === 0}
          >
            Новый шаблон
          </Button>
        </Stack>
      </Stack>

      {isLoading ? (
        <Stack alignItems="center" spacing={2} mt={6}>
          <CircularProgress />
          <Typography color="text.secondary">Загрузка шаблонов…</Typography>
        </Stack>
      ) : null}

      {loadingError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadingError.message || 'Не удалось загрузить список шаблонов'}
        </Alert>
      ) : null}

      {!isLoading && !loadingError && patterns.length === 0 ? (
        <Alert severity="info">Шаблоны обслуживания ещё не созданы.</Alert>
      ) : null}

      <Stack spacing={2}>
        {patterns.map((pattern) => (
          <Card key={pattern.id} variant="outlined">
            <CardHeader
              title={pattern.name}
              subheader={pattern.description || 'Описание не указано'}
              action={
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={pattern.isActive ? 'Активен' : 'Неактивен'}
                    color={pattern.isActive ? 'success' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={pattern.autoReset ? 'Авто-сброс' : 'Фиксированный порядок'}
                    color={pattern.autoReset ? 'info' : 'default'}
                    size="small"
                  />
                </Stack>
              }
            />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Последовательность очередей
                  </Typography>
                  <SequencePreview sequence={pattern.sequence} queueMap={queueMap} />
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Создан: {new Date(pattern.createdAt).toLocaleString('ru-RU')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Обновлён: {new Date(pattern.updatedAt).toLocaleString('ru-RU')}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => {
                  setEditingPattern(pattern);
                  setDrawerOpen(true);
                }}
              >
                Редактировать
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setPatternToDelete(pattern)}
              >
                Удалить
              </Button>
            </CardActions>
          </Card>
        ))}
      </Stack>

      <QueuePatternFormDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSubmit={handleSubmit}
        queues={queueOptions}
        loading={createMutation.isLoading || updateMutation.isLoading}
        pattern={editingPattern}
      />

      <Dialog open={Boolean(patternToDelete)} onClose={() => setPatternToDelete(null)}>
        <DialogTitle>Удалить шаблон</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Вы уверены, что хотите удалить шаблон «{patternToDelete?.name}»? Отменить действие будет невозможно.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPatternToDelete(null)}>Отмена</Button>
          <Button onClick={handleDelete} color="error" disabled={deleteMutation.isLoading}>
            {deleteMutation.isLoading ? 'Удаление…' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QueuePatternsPage;
